"use client";

import React from "react";
import { Heading } from "@/components/ui/typography";
import Image from "next/image";

type Props = {
  title?: string;
  subtitle?: string;
  primaryCtaText?: string;
  primaryCtaHref?: string;
  secondaryCtaText?: string;
  secondaryCtaHref?: string;
};

const DEFAULTS = {
  title:
    "How Hong Kong's Offshore Tax Exemption Lets You Pay 0% on Business Profits",
  subtitle:
    "If you've already formed a Hong Kong company but can't open a bank account, you're not alone. We help you choose the right bank, prepare documents properly, and submit an approval-ready application from the start.",
  primaryCtaText: "Book Your Free Strategy Call",
  primaryCtaHref:
    "https://calendly.com/d/cxy2-dy2-tqk/mutetaxes-consultation-call",
  secondaryCtaText: "Chat on WhatsApp",
  secondaryCtaHref:
    "https://api.whatsapp.com/send?phone=447445609826&text=Hi%20MuteTaxes%20team!%20I%27m%20interested%20in%20saving%20taxes%20and%20would%20like%20to%20know%20more",
};

export default function HeroSection({
  title = DEFAULTS.title,
  subtitle = DEFAULTS.subtitle,
  primaryCtaText = DEFAULTS.primaryCtaText,
  primaryCtaHref = DEFAULTS.primaryCtaHref,
  secondaryCtaText = DEFAULTS.secondaryCtaText,
  secondaryCtaHref = DEFAULTS.secondaryCtaHref,
}: Props = {}) {
  return (
    <section className="w-full bg-black py-[120px] lg:py-[120px] px-4 lg:px-0">
      <div className="relative max-w-[1200px] mx-auto flex flex-col lg:flex-row items-center justify-between gap-10">
        {/* IMAGE BLOCK (NOW RESPONSIVE + ALWAYS VISIBLE) */}
        <div className="w-full lg:flex-1 relative h-[250px] sm:h-[360px] md:h-[420px] lg:h-[506px] rounded-[30px] ">
          <Image
            src="/tax-optimization/tax-optimization-main.webp"
            alt="Hero Visual"
            fill
            className="object-cover rounded-[30px]"
            priority
          />
          <div className="block absolute right-0 top-5 lg:right-auto lg:top-auto lg:-left-[60px] lg:bottom-[-20px] w-[122px] h-[88px]">
            <Image
              src="/hero-2.webp"
              alt="decor"
              fill
              className="object-contain"
            />
          </div>
        </div>

        {/* CONTENT */}
        <div className="w-full lg:flex-1 flex flex-col gap-8">
          {/* TITLE + TEXT */}
          <div className="flex flex-col gap-6">
            <Heading as="h1" variant="hero" className="text-left">
              {title}
            </Heading>

            <p className="text-[15px] sm:text-[16px] lg:text-[18px] leading-6 text-white/70 font-inter">
              {subtitle}
            </p>
          </div>

          {/* CTA BUTTONS */}
          <div>
            <div className="flex flex-col sm:flex-row gap-4 mt-2">
              {/* Primary */}
              <a
                href={primaryCtaHref}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 w-fit bg-[#335CFF] text-white px-5 py-2.5 rounded-lg text-sm font-normal hover:opacity-90 transition"
              >
                {primaryCtaText}
                <span>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10.7965 9.99932L7.08398 6.28682L8.14448 5.22632L12.9175 9.99932L8.14448 14.7723L7.08398 13.7118L10.7965 9.99932Z"
                      fill="white"
                    />
                  </svg>
                </span>
              </a>

              {/* Secondary */}
              <a
                href={secondaryCtaHref}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center w-fit gap-2 border border-[#6fa6ff] text-[#6fa6ff] bg-transparent px-5 py-2.5 rounded-lg text-sm font-normal hover:bg-white/5 hover:border-transparent transition"
              >
                {secondaryCtaText}
                <span>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10.7965 9.99932L7.08398 6.28682L8.14448 5.22632L12.9175 9.99932L8.14448 14.7723L7.08398 13.7118L10.7965 9.99932Z"
                      fill="#6fa6ff"
                    />
                  </svg>
                </span>
              </a>
            </div>
            <p className="mt-[16px] text-white/70 text-[12px] font-inter">
              No obligation | 30-minute consultation | Tailored to your
              situation
            </p>
          </div>

          {/* TRUST SECTION */}
          <div className="flex flex-col items-start gap-3">
            {/* AVATARS */}
            <div className="flex items-center gap-3 shadow-[20px_40px_80px_0_rgba(45,51,81,0.12)] border border-white/10 px-3 py-2 rounded-full">
              <div className="flex -space-x-2">
                <Image
                  src="/avatar-1.svg"
                  alt=""
                  width={20}
                  height={20}
                  className="rounded-full border"
                />
                <Image
                  src="/avatar-2.svg"
                  alt=""
                  width={20}
                  height={20}
                  className="rounded-full border"
                />
                <Image
                  src="/avatar-3.svg"
                  alt=""
                  width={20}
                  height={20}
                  className="rounded-full border"
                />
              </div>

              <span className="text-xs sm:text-sm text-white/60">
                Trusted by international founders restructuring through Hong
                Kong
              </span>
            </div>

            {/* RATING */}
            <div className="flex items-center gap-2 flex-wrap">
              <Image src="/image-32.webp" alt="logo" width={48} height={16} />

              <div className="flex items-center gap-1 text-sm text-white/70">
                {[1, 2, 3, 4].map((i) => (
                  <Image
                    key={i}
                    src="/star-s-fill.svg"
                    alt="star"
                    width={16}
                    height={16}
                  />
                ))}

                {/* half star */}
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M8.00092 9.59365L9.69112 10.6256L9.23152 8.69965L10.7357 7.41145L8.76172 7.25305L8.00092 5.42485V9.59365ZM8.00092 11L4.47412 13.154L5.43292 9.13405L2.29492 6.44605L6.41392 6.11605L8.00092 2.30005L9.58792 6.11605L13.7075 6.44605L10.5689 9.13405L11.5277 13.154L8.00092 11Z"
                    fill="#FDC700"
                  />
                </svg>

                <span className="ml-1">4.5/5.0</span>
              </div>
            </div>
          </div>
        </div>

        {/* DECOR IMAGE (ONLY DESKTOP) */}
      </div>
    </section>
  );
}
